import { ChangeEvent, FC, useMemo, useRef } from 'react';

import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Button, ButtonProps } from '@shared/components/button-new';
import { FileType, getUploadingFileInfo, isValidFileType } from '@shared/components/file-dropzone';
import { showNotification } from '@shared/components/notification';

import { styles } from './FileUploadButton.styles';

// ************************************************************
// * !! DO NOT USE COMPONENT DIRECTLY                      !! *
// * !! USE SingleFileUploadButton / MultiFileUploadButton !! *
// ************************************************************

export interface FileUploadButtonProps extends WithStyles<typeof styles> {
  buttonProps: Omit<ButtonProps, 'classes'>;
  maxSize: number;
  multiple: boolean;
  type: FileType;
  onFileSelect: (files: File[] | File) => void;
  onReject?: (rejectedFiles: File[] | File) => void;
}

// ************************************************************
// * !! DO NOT USE COMPONENT DIRECTLY                      !! *
// * !! USE SingleFileUploadButton / MultiFileUploadButton !! *
// ************************************************************

const FileUploadButtonComponent: FC<FileUploadButtonProps> = ({
  buttonProps,
  classes,
  maxSize,
  multiple,
  type,
  onFileSelect,
  onReject,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadingFileInfo = useMemo(() => getUploadingFileInfo(type), [type]);

  const handleClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    // console.log('handleChange -> files', files);

    const validExtensions = uploadingFileInfo.acceptString.split(',').map((ext) => ext.trim());

    // Separate files into accepted and rejected based on size and format
    const acceptedFiles: File[] = [];
    const rejectedFiles: File[] = [];

    files.forEach((file) => {
      if (file.size <= maxSize && isValidFileType(file, validExtensions)) {
        acceptedFiles.push(file);
      } else {
        rejectedFiles.push(file);
      }
    });

    // console.log('handleChange -> acceptedFiles', acceptedFiles);
    // console.log('handleChange -> rejectedFiles', rejectedFiles);

    // Clear input value so we can upload same file again
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    // Handle file with size and format validation
    if (rejectedFiles.length > 0) {
      if (onReject) {
        // TODO: Refactor so this always supplies []. Correct in parent
        // @ts-ignore
        onReject(multiple ? rejectedFiles : rejectedFiles[0]);
      } else {
        showNotification(`Could not upload files. Please contact us for assistance`, 'error');
      }
    }

    if (acceptedFiles.length > 0) {
      // TODO: Refactor so this always supplies []. Correct in parent
      // @ts-ignore
      onFileSelect(multiple ? acceptedFiles : acceptedFiles[0]);
    }
  };

  return (
    <>
      <Button
        {...buttonProps}
        onClick={handleClick}
        tooltip={t('accepted_file_types_colon_xfiletypesx', { file_types: uploadingFileInfo.acceptTooltip })}
      />
      <input
        accept={uploadingFileInfo.acceptString}
        className={classes.input}
        multiple={multiple}
        ref={inputRef}
        type="file"
        onChange={handleChange}
      />
    </>
  );
};

// ************************************************************
// * !! DO NOT USE COMPONENT DIRECTLY                      !! *
// * !! USE SingleFileUploadButton / MultiFileUploadButton !! *
// ************************************************************

export const FileUploadButton = withStyles(styles)(FileUploadButtonComponent);